import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Loading from '../utils/Loading';
import { get_payload_insights, get_indicators_results } from '../../actions/indicatorsActions';
import normalizeAnswer from '../../utils/normalizeAnswer';
import { Icons } from '../payloadNew/Icons';
import IdChip from '../payloadNew/IdChip';
import '../payloadNew/payloadNew.css';
import './insights.css';

const TONE_KEY = { PASS: 'pass', FAIL: 'attention', 'N/A': 'na' };
const SECTION_ORDER = ['attention', 'pass', 'na'];

const PayloadInsightsDetails = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { location_id, payload_id, response_id } = useParams();
	const dispatch = useDispatch();

	// Same detail view backs two routes:
	//   /locations/:location_id/payloads/:payload_id/insights   → payload mode
	//   /surveys/responses/:response_id/insights                → survey mode
	// The route params tell us which one we're in; the rest just switches on it.
	const isSurvey = !!response_id;
	const entityId = isSurvey ? response_id : payload_id;

	const { payload_insights, payload_insights_loading, indicators_results, indicators_results_loading } =
		useSelector(state => state.indicators);
	const insights = isSurvey ? indicators_results : payload_insights;
	const insightsLoading = isSurvey ? indicators_results_loading : payload_insights_loading;

	const [filter, setFilter] = useState('all');
	const [search, setSearch] = useState('');

	useEffect(() => {
		if (!entityId) return;
		// Survey mode reuses the results/overview payload (which carries the
		// evaluated rules under `evaluations`); payload mode still hits the
		// dedicated insights endpoint.
		dispatch(isSurvey ? get_indicators_results(entityId) : get_payload_insights(entityId));
	}, [entityId, isSurvey]);

	const rules = useMemo(() => {
		// Payload responses expose rows under `.results`; survey responses under
		// `.evaluation.results` inside the indicators-results payload.
		const raw = isSurvey
			? (Array.isArray(insights?.evaluation?.results) ? insights.evaluation.results : [])
			: (Array.isArray(insights?.results) ? insights.results : []);
		return raw
			.map((r) => ({ ...r, answer: normalizeAnswer(r?.answer) }))
			.filter((r) => r.answer);
	}, [insights, isSurvey]);

	const counts = useMemo(() => {
		const c = { pass: 0, attention: 0, na: 0 };
		rules.forEach((r) => { c[TONE_KEY[r.answer]]++; });
		return c;
	}, [rules]);

	const total = rules.length;

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return rules.filter((r) => {
			const tone = TONE_KEY[r.answer];
			if (filter !== 'all' && filter !== tone) return false;
			if (!q) return true;
			return (`${r.question || ''} ${r.feedback || ''}`).toLowerCase().includes(q);
		});
	}, [rules, filter, search]);

	const grouped = useMemo(() => {
		const g = { attention: [], pass: [], na: [] };
		filtered.forEach((r) => { g[TONE_KEY[r.answer]].push(r); });
		return g;
	}, [filtered]);

	if (insightsLoading) {
		return <Loading msg={t('payload.payloadInsights.loading')} />;
	}

	const onBack = () => {
		if (isSurvey) {
			navigate(`/surveys/responses/${entityId}`);
			return;
		}
		const loc = location_id || insights?.location?.$oid;
		if (loc && entityId) {
			navigate(`/locations/${loc}/payloads/${entityId}`);
		} else {
			navigate(-1);
		}
	};

	const widthPct = (n) => (total > 0 ? (n / total) * 100 : 0);

	const sectionLabel = {
		attention: t('payload.payloadInsights.details.groups.fail'),
		pass: t('payload.payloadInsights.details.groups.pass'),
		na: t('payload.payloadInsights.details.groups.na')
	};
	const rowLabel = {
		attention: t('payload.payloadInsights.details.rowStatus.fail'),
		pass: t('payload.payloadInsights.details.rowStatus.pass'),
		na: t('payload.payloadInsights.details.rowStatus.na')
	};
	const SectionIcon = { attention: Icons.alert, pass: Icons.check, na: Icons.dash };

	return (
		<div id={localStorage.getItem('theme')}>
			<div className='payload-new'>
				<div className='pid-shell'>
					<button type='button' className='ds-back-link' onClick={onBack}>
						<Icons.arrow style={{ transform: 'rotate(180deg)' }} />
						{t('payload.payloadInsights.details.back')}
					</button>

					<section className='pn-card pid-summary-card'>
						<header className='pid-header'>
							<h1 className='pn-section-title pid-title'>
								<Icons.bulb /> {t('payload.payloadInsights.title')}
							</h1>
							<IdChip label='Id' value={entityId} full />
						</header>
						<div className='pid-summary'>
							<CountChip value={total} label={t('payload.payloadInsights.details.stripRules')} tone='neutral' />
							<CountChip value={counts.pass} label={t('payload.payloadInsights.details.stripPass')} tone='pass' />
							<CountChip value={counts.attention} label={t('payload.payloadInsights.details.stripFail')} tone='attention' />
							<CountChip value={counts.na} label={t('payload.payloadInsights.details.stripNa')} tone='na' />
							<div className='pid-summary-bar-wrap'>
								<div className='pid-summary-bar'>
									<span className='pid-bar-pass' style={{ width: `${widthPct(counts.pass)}%` }} />
									<span className='pid-bar-attention' style={{ width: `${widthPct(counts.attention)}%` }} />
									<span className='pid-bar-na' style={{ width: `${widthPct(counts.na)}%` }} />
								</div>
							</div>
						</div>
						<div className='pid-card-divider' />
						<div className='pid-filters'>
							<div className='pid-chips'>
								<FilterChip active={filter === 'all'} onClick={() => setFilter('all')}
									label={t('payload.payloadInsights.details.filters.all')} count={total} tone='primary' />
								<FilterChip active={filter === 'attention'} onClick={() => setFilter('attention')}
									label={t('payload.payloadInsights.details.filters.fail')} count={counts.attention} tone='attention' />
								<FilterChip active={filter === 'pass'} onClick={() => setFilter('pass')}
									label={t('payload.payloadInsights.details.filters.pass')} count={counts.pass} tone='pass' />
								<FilterChip active={filter === 'na'} onClick={() => setFilter('na')}
									label={t('payload.payloadInsights.details.filters.na')} count={counts.na} tone='na' />
							</div>
							<div className='pid-search'>
								<Icons.search />
								<input
									type='text'
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder={t('payload.payloadInsights.details.filters.searchPlaceholder')}
								/>
							</div>
						</div>
					</section>

					{SECTION_ORDER.map((tone) => {
						const rows = grouped[tone];
						if (!rows || rows.length === 0) return null;
						const Icon = SectionIcon[tone];
						return (
							<section key={tone} className='pn-card pid-group'>
								<div className='pn-section-header pid-group-head'>
									<h2 className={`pn-section-title pid-group-title pid-group-title--${tone}`}>
										<Icon /> {sectionLabel[tone]}
									</h2>
									<span className={`pid-group-count pid-group-count--${tone}`}>{rows.length}</span>
								</div>
								<div className='pid-group-body'>
									{rows.map((r, i) => (
										<RuleRow
											key={i}
											rule={r}
											tone={tone}
											isLast={i === rows.length - 1}
											statusLabel={rowLabel[tone]}
										/>
									))}
								</div>
							</section>
						);
					})}

					{filtered.length === 0 && (
						<section className='pn-card pid-empty'>
							{t('payload.payloadInsights.details.filters.noMatches')}
						</section>
					)}
				</div>
			</div>
		</div>
	);
};

const CountChip = ({ value, label, tone }) => (
	<div className={`pid-count pid-count--${tone}`}>
		{tone !== 'neutral' && <span className='pid-count-dot' />}
		<span className='pid-count-value'>{value}</span>
		<span className='pid-count-label'>{label}</span>
	</div>
);

const FilterChip = ({ active, onClick, label, count, tone }) => (
	<button
		type='button'
		className={`pid-chip pid-chip--${tone} ${active ? 'is-active' : ''}`}
		onClick={onClick}
	>
		{label}
		<span className='pid-chip-count'>{count}</span>
	</button>
);

const RuleRow = ({ rule, tone, isLast, statusLabel }) => {
	const IconNode = tone === 'pass' ? Icons.check : tone === 'attention' ? Icons.alert : Icons.dash;
	return (
		<div className={`pid-row ${isLast ? 'is-last' : ''}`}>
			<div className={`pid-row-icon pid-row-icon--${tone}`}>
				<IconNode />
			</div>
			<div className='pid-row-body'>
				<div className='pid-row-question'>{rule.question}</div>
				{rule.feedback && (
					<div className={`pid-row-feedback pid-row-feedback--${tone}`}>
						{rule.feedback}
					</div>
				)}
			</div>
			<div className={`pid-row-status pid-row-status--${tone}`}>
				{statusLabel}
			</div>
		</div>
	);
};

export default PayloadInsightsDetails;
